import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatApiError } from "../auth/AuthContext";
import { AuthCheckbox } from "../components/AuthCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 3-step secure reset:
//  1. ForgotPassword page issues a token via email.
//  2. This page opens with ?token=... and calls /validate on mount — user only
//     sees the new-password form if the token is genuinely valid + unexpired.
//  3. Submitting the form calls /confirm which is single-use.
export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = (sp.get("token") || "").trim();

  const [checking, setChecking] = useState(true);
  const [tokenState, setTokenState] = useState(null); // null | 'valid' | 'invalid'
  const [maskedEmail, setMaskedEmail] = useState("");
  const [tokenError, setTokenError] = useState("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);

  // Step 2 — validate the token as soon as the page loads
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setChecking(false);
        setTokenState("invalid");
        setTokenError("Missing reset token. Please request a new link from the Forgot Password page.");
        return;
      }
      try {
        const { data } = await axios.post(`${API}/auth/password-reset/validate`, { token });
        if (cancelled) return;
        setTokenState("valid");
        setMaskedEmail(data?.email_masked || "");
      } catch (e2) {
        if (cancelled) return;
        setTokenState("invalid");
        setTokenError(formatApiError(e2.response?.data?.detail) || "This reset link is invalid or has expired.");
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Step 3 — commit the new password
  const submit = async e => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1600);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-shell" data-testid="reset-page">
      <div className="auth-card">
        <Link to="/" className="auth-brand" data-testid="auth-brand-link">
          <img src="/logo.png" alt="GoPrintPack" className="auth-logo"/>
        </Link>
        <h1>Set a new password</h1>

        {checking && (
          <p className="auth-sub" data-testid="reset-validating">Verifying your reset link…</p>
        )}

        {!checking && tokenState === "invalid" && (
          <>
            <div className="auth-error" data-testid="reset-invalid-token">{tokenError}</div>
            <div className="auth-alt" style={{ marginTop: 16 }}>
              <Link to="/forgot-password" data-testid="reset-request-new" className="primary-btn full" style={{ textAlign: "center" }}>
                Request a new reset link
              </Link>
            </div>
            <div className="auth-alt">
              <Link to="/login" data-testid="back-to-login">← Back to sign in</Link>
            </div>
          </>
        )}

        {!checking && tokenState === "valid" && done && (
          <div className="auth-success" data-testid="reset-success">
            <p><b>Password updated.</b> Redirecting you to sign in…</p>
          </div>
        )}

        {!checking && tokenState === "valid" && !done && (
          <>
            <p className="auth-sub">
              {maskedEmail ? <>Choose a strong new password for <b>{maskedEmail}</b>.</> : "Choose a strong password. You'll be redirected to sign in once it's saved."}
            </p>
            <form onSubmit={submit} className="auth-form">
              <label>New password
                <input data-testid="reset-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" placeholder="At least 8 characters"/>
              </label>
              <label>Confirm password
                <input data-testid="reset-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password"/>
              </label>
              <AuthCheckbox testid="reset-show-password" checked={showPw} onChange={setShowPw} label="Show password"/>
              {err && <div className="auth-error" data-testid="reset-error">{err}</div>}
              <button data-testid="reset-submit" className="primary-btn full" disabled={busy}>
                {busy ? "Saving…" : "Save new password"}
              </button>
              <div className="auth-alt">
                <Link to="/login" data-testid="back-to-login">← Back to sign in</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
